/**
 * Software Evaluation Form (Public - No Auth Required)
 * 
 * Public-facing form for potential customers to submit their requirements.
 * Based on the Aegis Scope questionnaire.
 */

import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Icons
const AlertCircle = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const Loader = ({ className = "w-5 h-5" }) => (
  <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const CheckCircle = ({ className = "w-16 h-16" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default function SoftwareEvaluationForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initialFormState = {
    // Company Information
    company_name: '',
    company_contact: '',
    email: '',
    
    // Pain Points & Current State
    top_pain_points: '',
    time_searching_percentage: '',
    asset_missing_frequency: '',
    current_tracking_system: '',
    current_system_limitations: '',
    missing_features: '',
    annual_replacement_cost: '',
    unauthorized_theft: null,
    
    // Asset & Equipment Information
    asset_types_to_track: '',
    other_equipment_types: '',
    total_assets_count: '',
    high_value_assets: '',
    
    // Facility Information
    facility_square_footage: '',
    forklift_count: '',
    device_installation_locations: '',
    prohibited_zones: '',
    
    // IT Infrastructure
    has_wifi: null,
    existing_it_infrastructure: '',
    has_three_phase_electrical: null,
    cyber_security_protocol: '',
    
    // Tracking Requirements
    plx_tags_count: '',
    gps_tracking_count: '',
    inter_facility_movement: null,
    accuracy_metric: '',
    facilities_count: '',
    visibility_type: '',
    supplier_visibility_needs: '',
    
    // User Access & Integration
    user_count: '',
    user_roles: '',
    different_permission_levels: null,
    needs_production_sequencing: null,
    current_production_system: '',
    integration_type: '',
    
    // Implementation Timeline
    ideal_timeline: '',
    seasonal_considerations: '',
    implementation_scope: '',
    prefer_pilot: null,
    pilot_area: '',
    pilot_success_metrics: '',
    
    // Requirements & Budget
    critical_features: '',
    deal_breakers: '',
    budget_range: '',
    pricing_model: '',
    decision_makers: '',
    roi_measurement: '',
    success_6_months: '',
    success_12_months: '',
    
    // Metadata
    referrer_name: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (type === 'radio') {
      setFormData(prev => ({
        ...prev,
        [name]: value === 'true' ? true : value === 'false' ? false : value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/software-evaluation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          task_owner: formData.email
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit form');
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#e6e7e8] flex items-center justify-center p-4 py-12">
        <div className="bg-white rounded-3xl shadow-xl p-12 max-w-xl w-full text-center border border-[#3374ba]/10">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#272c3a] mb-4">Form Submitted!</h2>
          <p className="text-gray-500 mb-8 text-lg leading-relaxed">
            Thank you for sharing your requirements. Our specialists will review your data and build a custom proposal tailored to your facility's needs.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData(initialFormState);
            }}
            className="bg-[#3374ba] text-white px-8 py-3 rounded-xl hover:bg-[#2c65a3] transition-all font-bold shadow-lg shadow-blue-500/20"
          >
            Submit Another Evaluation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e6e7e8] py-12">
      <div className="max-w-4xl mx-auto pb-20 px-4">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src="/aegislogo.png" alt="Aegis Logo" className="h-16 md:h-20" />
        </div>

        {/* Header Card */}
        <div className="bg-[#272c3a] rounded-3xl shadow-xl p-8 md:p-12 mb-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#3374ba] opacity-10 -mr-20 -mt-20 rounded-full blur-3xl"></div>
        <div className="relative z-10">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">Aegis Scope Evaluation</h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Let's build your optimized asset tracking ecosystem. Fill out the details below to help us understand your specific operational challenges.
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-12 rounded-2xl shadow-sm">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-red-500 mr-4 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-bold text-lg">Submission Error</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-10">
          {/* Company Information */}
          <FormSection title="Company Insights" subtitle="Tell us about your organization">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Company Name *" name="company_name" value={formData.company_name} onChange={handleChange} required />
              <Input label="Primary Contact *" name="company_contact" value={formData.company_contact} onChange={handleChange} required />
              <Input label="Work Email *" name="email" type="email" value={formData.email} onChange={handleChange} required className="md:col-span-2" />
            </div>
          </FormSection>

          {/* Pain Points & Current State */}
          <FormSection title="Operational Pain Points" subtitle="Identify current bottlenecks">
            <TextArea label="What are your top 3 asset tracking/repair pain points today?" name="top_pain_points" value={formData.top_pain_points} onChange={handleChange} rows={4} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Search Time Percentage (%)" name="time_searching_percentage" type="number" value={formData.time_searching_percentage} onChange={handleChange} placeholder="e.g. 15" />
              <Select label="Frequency of Missing Assets" name="asset_missing_frequency" value={formData.asset_missing_frequency} onChange={handleChange} options={[
                { value: '', label: 'Select frequency...' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'rarely', label: 'Rarely' }
              ]} />
            </div>
            <Input label="Current Tracking System" name="current_tracking_system" value={formData.current_tracking_system} onChange={handleChange} placeholder="e.g. Spreadsheets, Manual, N/A" />
            <TextArea label="Limitations of current solution" name="current_system_limitations" value={formData.current_system_limitations} onChange={handleChange} rows={3} />
            <TextArea label="Missing features/capabilities" name="missing_features" value={formData.missing_features} onChange={handleChange} rows={3} />
            <Input label="Estimated annual replacement cost ($)" name="annual_replacement_cost" type="number" value={formData.annual_replacement_cost} onChange={handleChange} placeholder="50000" />
            <RadioGroup label="Do you experience unauthorized theft?" name="unauthorized_theft" value={formData.unauthorized_theft} onChange={handleChange} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
          </FormSection>

          {/* Asset & Equipment Information */}
          <FormSection title="Asset Specifications" subtitle="What are we tracking?">
            <TextArea label="Asset Types (racks, tools, containers, etc.)" name="asset_types_to_track" value={formData.asset_types_to_track} onChange={handleChange} rows={3} placeholder="e.g., expendables, racks, containers, tools" />
            <TextArea label="Other Equipment Types" name="other_equipment_types" value={formData.other_equipment_types} onChange={handleChange} rows={3} placeholder="e.g., welding equipment, robotic arms" />
            <Input label="Total Asset Count" name="total_assets_count" type="number" value={formData.total_assets_count} onChange={handleChange} placeholder="5000" />
            <TextArea label="High-value/Critical assets" name="high_value_assets" value={formData.high_value_assets} onChange={handleChange} rows={2} placeholder="Describe any specific assets requiring priority tracking" />
          </FormSection>

          {/* Facility Information */}
          <FormSection title="Facility & IT Landscape" subtitle="Infrastructure requirements">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Facility Square Footage" name="facility_square_footage" type="number" value={formData.facility_square_footage} onChange={handleChange} placeholder="185000" />
              <Input label="Active Forklifts Count" name="forklift_count" type="number" value={formData.forklift_count} onChange={handleChange} placeholder="10" />
            </div>
            <TextArea label="Device Installation Locations" name="device_installation_locations" value={formData.device_installation_locations} onChange={handleChange} rows={3} placeholder="Where can we install tracking devices/hubs?" />
            <TextArea label="Prohibited Zones" name="prohibited_zones" value={formData.prohibited_zones} onChange={handleChange} rows={2} placeholder="Any areas where devices cannot be installed?" />
            <RadioGroup label="Is there facility-wide WiFi?" name="has_wifi" value={formData.has_wifi} onChange={handleChange} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            <TextArea label="Existing IT Infrastructure" name="existing_it_infrastructure" value={formData.existing_it_infrastructure} onChange={handleChange} rows={3} placeholder="Our software is independent, but requires internet access points" />
            <RadioGroup label="Facility has 3-phase electrical?" name="has_three_phase_electrical" value={formData.has_three_phase_electrical} onChange={handleChange} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            <TextArea label="Cyber Security Protocols" name="cyber_security_protocol" value={formData.cyber_security_protocol} onChange={handleChange} rows={3} placeholder="Any IT security protocols we should be aware of?" />
          </FormSection>

          {/* Tracking Requirements */}
          <FormSection title="Tracking Requirements" subtitle="Precision and visibility">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Estimated PLX Tags Needed" name="plx_tags_count" type="number" value={formData.plx_tags_count} onChange={handleChange} placeholder="2500" />
              <Input label="GPS Tracking Units" name="gps_tracking_count" type="number" value={formData.gps_tracking_count} onChange={handleChange} placeholder="250 (We recommend 10%+)" />
            </div>
            <RadioGroup label="Inter-facility movement?" name="inter_facility_movement" value={formData.inter_facility_movement} onChange={handleChange} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            <Input label="Desired Accuracy Metric" name="accuracy_metric" value={formData.accuracy_metric} onChange={handleChange} placeholder="e.g., 20 ft" />
            <Input label="Number of Facilities" name="facilities_count" type="number" value={formData.facilities_count} onChange={handleChange} placeholder="5" />
            <RadioGroup label="Visibility Type" name="visibility_type" value={formData.visibility_type} onChange={handleChange} options={[{ value: 'independent', label: 'Independent Systems' }, { value: 'shared', label: 'Shared Visibility' }]} />
            <TextArea label="Supplier Visibility Needs" name="supplier_visibility_needs" value={formData.supplier_visibility_needs} onChange={handleChange} rows={3} placeholder="Do you need tracking when assets are at supplier locations?" />
          </FormSection>

          {/* User Access & Integration */}
          <FormSection title="Integration & Users" subtitle="Access and connectivity">
            <Input label="Target User Count" name="user_count" type="number" value={formData.user_count} onChange={handleChange} placeholder="10" />
            <TextArea label="Anticipated User Roles" name="user_roles" value={formData.user_roles} onChange={handleChange} rows={2} placeholder="e.g., Ops, Logistics, Management, Procurement" />
            <RadioGroup label="Different Permission Levels?" name="different_permission_levels" value={formData.different_permission_levels} onChange={handleChange} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            <RadioGroup label="Production Sequencing Needed?" name="needs_production_sequencing" value={formData.needs_production_sequencing} onChange={handleChange} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            <Input label="Existing Production System" name="current_production_system" value={formData.current_production_system} onChange={handleChange} placeholder="e.g., ZOHO, SAP, Oracle, Custom" />
            <RadioGroup label="Integration Strategy" name="integration_type" value={formData.integration_type} onChange={handleChange} options={[{ value: 'integrated', label: 'Full Integration' }, { value: '3rd_party', label: 'Standalone/3rd Party' }]} />
          </FormSection>

          {/* Implementation Timeline */}
          <FormSection title="Implementation & Timeline" subtitle="Deployment strategy">
            <Input label="Ideal Deployment Timeline" name="ideal_timeline" value={formData.ideal_timeline} onChange={handleChange} placeholder="e.g., Immediate, Q3 2026" />
            <TextArea label="Seasonal Considerations" name="seasonal_considerations" value={formData.seasonal_considerations} onChange={handleChange} rows={3} placeholder="Any seasonal considerations or schedules to work with?" />
            <TextArea label="Implementation Scope" name="implementation_scope" value={formData.implementation_scope} onChange={handleChange} rows={2} placeholder="Start with one area or facility-wide?" />
            <RadioGroup label="Start with a Pilot?" name="prefer_pilot" value={formData.prefer_pilot} onChange={handleChange} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            {formData.prefer_pilot && (
              <>
                <Input label="Target Pilot Area" name="pilot_area" value={formData.pilot_area} onChange={handleChange} placeholder="Which area or asset type?" />
                <TextArea label="Pilot Success Metrics" name="pilot_success_metrics" value={formData.pilot_success_metrics} onChange={handleChange} rows={3} placeholder="How will you evaluate the pilot?" />
              </>
            )}
          </FormSection>

          {/* Requirements & Budget */}
          <FormSection title="Requirements & Budget" subtitle="Critical needs and investment">
            <TextArea label="Critical Features" name="critical_features" value={formData.critical_features} onChange={handleChange} rows={3} placeholder="What features are absolutely critical for your operations?" />
            <TextArea label="Deal-breakers" name="deal_breakers" value={formData.deal_breakers} onChange={handleChange} rows={3} placeholder="Any absolute deal-breakers?" />
            <Input label="Estimated Budget Range" name="budget_range" value={formData.budget_range} onChange={handleChange} placeholder="e.g., $50k-$100k, Flexible" />
            <RadioGroup label="Preferred Pricing Model" name="pricing_model" value={formData.pricing_model} onChange={handleChange} options={[{ value: 'purchase', label: 'Direct Purchase' }, { value: 'subscription', label: 'Subscription with Support' }]} />
            <TextArea label="Key Decision Makers" name="decision_makers" value={formData.decision_makers} onChange={handleChange} rows={3} placeholder="Names & Titles of decision makers" />
            <TextArea label="ROI Measurement" name="roi_measurement" value={formData.roi_measurement} onChange={handleChange} rows={3} placeholder="How will ROI be measured?" />
            <TextArea label="Success at 6 Months" name="success_6_months" value={formData.success_6_months} onChange={handleChange} rows={3} placeholder="What does success look like in 6 months?" />
            <TextArea label="Success at 12 Months" name="success_12_months" value={formData.success_12_months} onChange={handleChange} rows={3} placeholder="What does success look like in 12 months?" />
          </FormSection>

          {/* Additional Information */}
          <FormSection title="Additional Information" subtitle="Optional details">
            <Input label="Referrer Name (Optional)" name="referrer_name" value={formData.referrer_name} onChange={handleChange} placeholder="Who referred you to Aegis?" />
          </FormSection>

          {/* Submit Button */}
          <div className="mt-10">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#f7923f] text-white py-5 px-8 rounded-2xl font-black text-xl hover:bg-[#e08535] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center shadow-2xl shadow-orange-500/30 active:scale-95"
            >
              {loading ? (
                <>
                  <Loader className="w-6 h-6 mr-3" />
                  Analyzing Data...
                </>
              ) : (
                'Submit Professional Evaluation'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper Components
function FormSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-3xl shadow-lg border border-[#3374ba]/5 p-8 md:p-10">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#272c3a]">{title}</h2>
        <p className="text-sm font-medium text-[#f7923f] uppercase tracking-widest mt-1">{subtitle}</p>
      </div>
      <div className="space-y-6">
        {children}
      </div>
    </div>
  );
}

function Input({ label, name, type = 'text', value, onChange, required = false, placeholder = '', className = '' }) {
  return (
    <div className={className}>
      <label className="block text-sm font-bold text-[#272c3a] mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full px-5 py-3 bg-[#e6e7e8]/50 border-2 border-transparent rounded-xl focus:border-[#3374ba] focus:bg-white transition-all outline-none text-[#272c3a]"
      />
    </div>
  );
}

function TextArea({ label, name, value, onChange, rows = 3, placeholder = '' }) {
  return (
    <div>
      <label className="block text-sm font-bold text-[#272c3a] mb-2">{label}</label>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-5 py-3 bg-[#e6e7e8]/50 border-2 border-transparent rounded-xl focus:border-[#3374ba] focus:bg-white transition-all outline-none text-[#272c3a] resize-none"
      />
    </div>
  );
}

function Select({ label, name, value, onChange, options }) {
  return (
    <div>
      <label className="block text-sm font-bold text-[#272c3a] mb-2">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-5 py-3 bg-[#e6e7e8]/50 border-2 border-transparent rounded-xl focus:border-[#3374ba] focus:bg-white transition-all outline-none text-[#272c3a] appearance-none cursor-pointer"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function RadioGroup({ label, name, value, onChange, options }) {
  return (
    <div>
      <label className="block text-sm font-bold text-[#272c3a] mb-3">{label}</label>
      <div className="flex flex-wrap gap-4">
        {options.map(option => {
          const isChecked = value === option.value;
          return (
            <label 
              key={`${name}-${option.value}`} 
              className={`flex items-center cursor-pointer px-6 py-3 rounded-xl border-2 transition-all ${
                isChecked 
                  ? 'bg-[#3374ba]/10 border-[#3374ba]' 
                  : 'bg-white border-transparent hover:bg-[#e6e7e8]/30'
              }`}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isChecked}
                onChange={onChange}
                className="hidden"
              />
              <span className={`text-sm font-bold ${isChecked ? 'text-[#3374ba]' : 'text-gray-500'}`}>
                {option.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
